import { useEffect, useState } from "react";

import { ANIMATION_TIMINGS } from "../constants";

interface CreditApplicationAnimationState {
  isCreditScoreAnimating: boolean;
  isCreditLineItemVisible: boolean;
}

interface UseCreditApplicationAnimationParams {
  /**
   * Credit amount to add to the score (positive number)
   */
  creditAmount: number;
  /**
   * Score after the initial animation sequence completes
   */
  baseScore: number;
  /**
   * Whether the credit application animation has been triggered (user clicked "Apply")
   */
  isTriggered: boolean;
  /**
   * Called when the full animation sequence completes
   */
  onComplete: () => void;
}

interface UseCreditApplicationAnimationResult {
  /**
   * The target end score for the credit animation
   */
  creditEndScore: number;
  /**
   * Whether the score counter is currently animating the credit
   */
  isCreditScoreAnimating: boolean;
  /**
   * Whether the credit line item should be visible
   */
  isCreditLineItemVisible: boolean;
  /**
   * Label for the progress bar during credit application
   */
  progressBarLabel: string;
}

/**
 * Timer-based animation hook for the credit application sequence.
 * Reuses the same timing constants as useScoreAnimationSequence.
 *
 * Sequence when triggered:
 * 1. Immediately: score animation starts, line item visible, label = "Applying credit..."
 * 2. After APPLY_CREDIT_SCORE_ANIMATION_DURATION_MS: score animation ends, label = "Credit applied!", complete
 */
export function useCreditApplicationAnimation(
  params: UseCreditApplicationAnimationParams
): UseCreditApplicationAnimationResult {
  const { creditAmount, baseScore, isTriggered, onComplete } = params;

  const [animationState, setAnimationState] = useState<CreditApplicationAnimationState>({
    isCreditScoreAnimating: false,
    isCreditLineItemVisible: false,
  });

  const [progressBarLabel, setProgressBarLabel] = useState("");

  useEffect(() => {
    if (!isTriggered) {
      return;
    }

    // Step 1: Immediately start score animation and show line item
    setAnimationState({
      isCreditScoreAnimating: true,
      isCreditLineItemVisible: true,
    });
    setProgressBarLabel("Applying credit...");

    // Step 2: Score animation ends, mark complete
    const timer = setTimeout(() => {
      setAnimationState((previous) => ({
        ...previous,
        isCreditScoreAnimating: false,
      }));
      setProgressBarLabel("Credit applied!");
      onComplete();
    }, ANIMATION_TIMINGS.APPLY_CREDIT_SCORE_ANIMATION_DURATION_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [isTriggered, onComplete]);

  const creditEndScore = baseScore + creditAmount;

  return {
    creditEndScore,
    isCreditScoreAnimating: animationState.isCreditScoreAnimating,
    isCreditLineItemVisible: animationState.isCreditLineItemVisible,
    progressBarLabel,
  };
}
